import { useState, useEffect } from "react";
import { Flex, Switch, Text } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  ActionMenu,
  ActionDrawerItems,
  DrawerList,
  Icon,
} from "barte-design-system";
import { PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { useSettings, useIntegration } from "@/hooks";
import { formatDate, showToast } from "@/utils";
import { FormModal, Input } from "@/components";
import { EditSellerTokenActive, SellerTokens } from "@/models";

interface TokenItemProps {
  token: SellerTokens;
}
const tokenCreateScheama = yup.object().shape({
  title: yup.string().required("Preenchimento obrigatório!"),
});

export const TokenItem = ({ token }: TokenItemProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { putSellerApiToken } = useSettings();
  const { handleModal, isLoading, setIsLoading, getTokensInitialData } = useIntegration();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(tokenCreateScheama),
  });
  const getTitleValue = watch("title");

  useEffect(() => {
    if (isEditModalOpen) {
      setValue("title", token.title);
    }
  }, [isEditModalOpen, token.title, setValue]);

  const onSubmit = handleSubmit(async (data: EditSellerTokenActive) => {
    try {
      setIsLoading(true);
      await putSellerApiToken(token.tokenId, {
        title: data.title,
        active: token.active,
      });
      getTokensInitialData();
      showToast({ message: "Token atualizado com sucesso!", type: "success" });
    } catch (error) {
      error?.response?.data?.errors?.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    } finally {
      setIsEditModalOpen(!isEditModalOpen);
      setIsLoading(false);
    }
  });

  return (
    <>
      <FormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(!isEditModalOpen);
        }}
        title="Editar chave API"
        handleConfirmModal={onSubmit}
        isSubmitting={isLoading}
        confirmationButtonText="Salvar"
        disableWhenIsFormEmpty={getTitleValue === ""}
      >
        <Input
          name="title"
          register={{ ...register("title") }}
          errors={errors?.title}
        />
        
      </FormModal>

      <Flex
        w="100%"
        bgColor="gray.25"
        p="8px 16px"
        align="center"
        gap={6}
        borderRadius="4px"
      >
        <Text flex="1" color="var(--content-primary)">
          {token.title}
        </Text>
        <Flex direction="column" w="110px">
          <Text fontSize="12px" color="var(--content-secondary)">
            Chave
          </Text>
          <Text fontSize="14px" color="var(--content-primary)">
            {token.uuid}
          </Text>
        </Flex>
        <Flex direction="column" w="130px">
          <Text fontSize="12px" color="var(--content-secondary)">
            Expiração
          </Text>
          <Text fontSize="14px" color="var(--content-primary)">
            {token.expiresAt ? formatDate(token.expiresAt) : "Sem expiração"}
          </Text>
        </Flex>
        <Flex direction="column" w="130px">
          <Text fontSize="12px" color="var(--content-secondary)">
            Último uso
          </Text>
          <Text fontSize="14px" color="var(--content-primary)">
            {token.lastUsedAt ? formatDate(token.lastUsedAt) : "Nunca usado"}
          </Text>
        </Flex>
        <ActionMenu align="right">
          <DrawerList>
            <ActionDrawerItems
              title=""
              items={[
                {
                  label: "Editar",
                  variant: "icon" as const,
                  icon: <Icon icon={PencilSimpleIcon} size={16} />,
                  onClick: () => setIsEditModalOpen(true),
                },
                {
                  label: "Excluir",
                  variant: "icon" as const,
                  icon: <Icon icon={TrashIcon} size={16} />,
                  onClick: () => handleModal({ type: "delete", token }),
                },
              ]}
            />
          </DrawerList>
        </ActionMenu>
        <Switch
          isChecked={token.active}
          isDisabled={!token.active}
          onChange={() => handleModal({ type: "changeActive", token })}
        />
      </Flex>
    </>
  );
};
